// ─── Hook : ÉVÉNEMENTS SUPERPOSÉS multi-calendriers ───────────────
// Extrait d'EcranCalendrier (découpage). Écoute les événements + récurrences +
// grilles d'EDT de TOUS les calendriers visibles sur la fenêtre affichée,
// fusionne (services/evenements), génère l'EDT à la volée (dédup MoPCal) et
// teinte chaque item par la couleur de son calendrier (_couleur / _calId).
// Renvoie aussi les récurrences du calendrier ACTIF (page « Récurrences »).
import { useEffect, useState } from 'react';
import {
  ecouterEvenements, ecouterEvenementsRecurrents, fusionner,
} from '../services/evenements';
import { ecouterGrillesEDT, genererOccurrencesEDT } from '../services/edt';

export default function useEvenementsSuperposes({ visiblesIds, calendriers, couleurDe, fenetre, calId }) {
  const [map, setMap] = useState({});   // { dk: [items] } superposés, teintés
  const [recs, setRecs] = useState([]); // récurrences du calendrier ACTIF
  const [erreurRecs, setErreurRecs] = useState('');   // erreur de lecture, distincte (page « Récurrences »)
  // Refus de règle sur un des calendriers superposés : la grille se remplit
  // quand même avec ce qui a pu être lu — ce n'est PAS « pas d'événement ce
  // mois-ci ». Même correction que sur les contacts/le journal/la messagerie ;
  // ici plusieurs calendriers/sources peuvent échouer en même temps, d'où la
  // carte plutôt qu'un simple booléen.
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    const ids = visiblesIds;
    if (!ids.length) { setMap({}); setErreur(''); return undefined; }
    const parCal = {};
    const erreurs = {};
    const majErreur = () => setErreur(Object.values(erreurs).filter(Boolean).join(' · '));
    const recompute = () => {
      const out = {};
      for (const cid of ids) {
        const d = parCal[cid]; if (!d) continue;
        const m = fusionner(d.eves || {}, d.recs || [], fenetre.d1, fenetre.d2);
        // EDT : occurrences GÉNÉRÉES à la volée — sauf les jours déjà
        // MATÉRIALISÉS par MoPCal (source 'edt' / grilleId), pas de doublons.
        const perVac = (calendriers.find(cc => cc.id === cid)?.vacancesConfig?.periodes || []).filter(p => p.actif !== false);
        const mEdt = genererOccurrencesEDT(d.edt || [], fenetre.d1, fenetre.d2, perVac);
        for (const dk of Object.keys(mEdt)) {
          const dejaMaterialise = (m[dk] || []).some(it => it.source === 'edt' || it.grilleId);
          if (!dejaMaterialise) m[dk] = [...(m[dk] || []), ...mEdt[dk]];
        }
        const coul = couleurDe(cid);
        // Copie issue d'un déplacement d'occurrence : re-liée à sa série pour
        // l'affichage (fiche de récurrence, ✏️ = éditer la série) — flag
        // estDeplace pour router suppression/déplacement vers la COPIE.
        const recsParId = {}; (d.recs || []).forEach(r => { recsParId[r.id] = r; });
        const lier = (it) => {
          const r = it.recIdOrigine && recsParId[it.recIdOrigine];
          if (!r) return it;
          return { ...it, isRecurrent: true, estDeplace: true, recId: r.id, recDef: r.recurrence, recExceptions: r.exceptions || [], deplacements: r.deplacements || {} };
        };
        for (const dk of Object.keys(m)) (out[dk] = out[dk] || []).push(...m[dk].map(it => ({ ...lier(it), _calId: cid, _couleur: coul })));
      }
      setMap(out);
    };
    const unsubs = [];
    ids.forEach(cid => {
      parCal[cid] = { eves: {}, recs: [], edt: [] };
      const nomCal = calendriers.find(c => c.id === cid)?.nom || cid;
      unsubs.push(ecouterEvenements(cid, e => { parCal[cid].eves = e; recompute(); }, { min: fenetre.min, max: fenetre.max },
        (msg) => { erreurs[`${cid}:eves`] = `${nomCal} : ${msg}`; majErreur(); }));
      unsubs.push(ecouterEvenementsRecurrents(cid, r => { parCal[cid].recs = r; recompute(); },
        (msg) => { erreurs[`${cid}:recs`] = `${nomCal} : ${msg}`; majErreur(); }));
      unsubs.push(ecouterGrillesEDT(cid, g => { parCal[cid].edt = g; recompute(); },
        (msg) => { erreurs[`${cid}:edt`] = `${nomCal} : ${msg}`; majErreur(); }));
    });
    return () => unsubs.forEach(u => u && u());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblesIds.join('|'), fenetre.min, fenetre.max]);

  // Récurrences du calendrier ACTIF (pour la page « Récurrences »).
  useEffect(() => {
    if (!calId) { setRecs([]); setErreurRecs(''); return undefined; }
    setErreurRecs('');
    return ecouterEvenementsRecurrents(calId, setRecs, setErreurRecs);
  }, [calId]);

  return { map, recs, erreur, erreurRecs };
}
